/**
 * useStaffWelcome — ViewModel for /staff/welcome.
 *
 * Per SOP 02 §"MVVM Pattern" + SOP 15 Ch.20 (SOLID — S/D). Owns the two
 * pre-auth fetches that back the staff-welcome flow:
 *
 *   1. verify(token) — GET /auth/staff/welcome/verify to confirm the
 *      invite token and pull the greeting name. Drives the page's
 *      verifying → form | expired | error transition.
 *   2. confirm(token, password) — POST /auth/staff/welcome/confirm to
 *      set the password, activate the staff row, and receive the
 *      landing path for the operator's user type.
 *
 * Both endpoints are pre-auth (no session cookie yet on the verify
 * step; the confirm step sets one), so the hook uses plain `fetch`
 * with `credentials:'include'` rather than `authedFetch`.
 */

import { useCallback, useState } from 'react'
import { APIBASE } from '../../constants/api.constants'
import { APIROUTES } from '../../constants/routes.constants'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../../utils/api/server-unavailable'

interface VerifyOk {
  readonly success: true
  readonly data: {
    readonly email: string
    readonly firstName: string | null
    readonly lastName: string | null
  }
}

interface ConfirmOk {
  readonly success: true
  readonly data: { readonly landingPath: string }
}

interface ErrBody {
  readonly success: false
  readonly error: { readonly message: string }
}

export interface VerifyResult {
  readonly email: string
  readonly firstName: string | null
  readonly lastName: string | null
}

export type VerifyOutcome =
  | { readonly ok: true; readonly data: VerifyResult }
  | { readonly ok: false; readonly message: string }

export type ConfirmOutcome =
  | { readonly ok: true; readonly landingPath: string }
  | { readonly ok: false; readonly message: string }

export interface UseStaffWelcomeReturn {
  readonly isVerifying: boolean
  readonly verifyError: string | null
  readonly verify: (token: string, signal?: AbortSignal) => Promise<VerifyOutcome>
  readonly confirm: (token: string, password: string, confirmPassword: string) => Promise<ConfirmOutcome>
}

export function useStaffWelcome(): UseStaffWelcomeReturn {
  const [isVerifying, setIsVerifying] = useState<boolean>(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)

  const verify = useCallback(
    async (token: string, signal?: AbortSignal): Promise<VerifyOutcome> => {
      setIsVerifying(true)
      setVerifyError(null)
      try {
        const url = `${APIBASE}${APIROUTES.AUTHSTAFFWELCOMEVERIFY}?token=${encodeURIComponent(token)}`
        const init: RequestInit = signal === undefined
          ? { credentials: 'include' }
          : { credentials: 'include', signal }
        let res: Response
        try {
          res = await fetch(url, init)
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') {
            return { ok: false, message: 'aborted' }
          }
          if (isNetworkError(err)) {
            setVerifyError(SERVER_UNAVAILABLE_MESSAGE)
            return { ok: false, message: SERVER_UNAVAILABLE_MESSAGE }
          }
          throw err
        }
        if (res.status >= 500) {
          setVerifyError(SERVER_UNAVAILABLE_MESSAGE)
          return { ok: false, message: SERVER_UNAVAILABLE_MESSAGE }
        }
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ErrBody | null
          // 4xx with valid envelope → real domain error (expired token,
          // already activated). Otherwise the server isn't talking to us
          // in our envelope shape — treat as infrastructure.
          const msg = body?.error.message ?? SERVER_UNAVAILABLE_MESSAGE
          setVerifyError(msg)
          return { ok: false, message: msg }
        }
        const body = (await res.json()) as VerifyOk
        return { ok: true, data: body.data }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return { ok: false, message: 'aborted' }
        }
        const msg = err instanceof Error ? err.message : SERVER_UNAVAILABLE_MESSAGE
        setVerifyError(msg)
        return { ok: false, message: msg }
      } finally {
        setIsVerifying(false)
      }
    },
    []
  )

  const confirm = useCallback(
    async (token: string, password: string, confirmPassword: string): Promise<ConfirmOutcome> => {
      let res: Response
      try {
        res = await fetch(`${APIBASE}${APIROUTES.AUTHSTAFFWELCOMECONFIRM}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token, password, confirmPassword })
        })
      } catch (err) {
        if (isNetworkError(err)) {
          return { ok: false, message: SERVER_UNAVAILABLE_MESSAGE }
        }
        return { ok: false, message: err instanceof Error ? err.message : SERVER_UNAVAILABLE_MESSAGE }
      }
      if (res.status >= 500) {
        return { ok: false, message: SERVER_UNAVAILABLE_MESSAGE }
      }
      try {
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ErrBody | null
          return {
            ok: false,
            message: body?.error.message ?? SERVER_UNAVAILABLE_MESSAGE
          }
        }
        const body = (await res.json()) as ConfirmOk
        return { ok: true, landingPath: body.data.landingPath }
      } catch (err) {
        return {
          ok: false,
          message: err instanceof Error ? err.message : SERVER_UNAVAILABLE_MESSAGE
        }
      }
    },
    []
  )

  return { isVerifying, verifyError, verify, confirm }
}
